// functions/src/utils/urlValidationShared.ts

/**
 * URL文字列をデコード結果が変わらなくなるまで再帰的にデコードする。
 * 無限ループ防止のため最大回数（10回）を設ける。
 */
// 完全な形（%XX、XXは16進2桁）のpercentエスケープ。これが残っていない文字列は、
// 末尾に単独の"%"が残っていてもそれはデコード済みの結果に含まれるリテラル文字であり、
// これ以上デコードすべき対象ではない（例: "100%25" → 1回のデコードで "100%" となった
// 時点でデコード完了。ここで decodeURIComponent を再度呼ぶと、実在するエスケープが
// 存在しないにもかかわらず不正なシーケンスとして例外になってしまう）。
const PERCENT_ENCODING_PATTERN = /%[0-9a-fA-F]{2}/

export function decodeUrlRepeatedly(
    url: string,
    maxIterations = 10
): { decoded: string; success: boolean } {
    let decoded = url
    let prev = ''
    let iterations = 0
    while (decoded !== prev && iterations < maxIterations) {
        if (!PERCENT_ENCODING_PATTERN.test(decoded)) {
            break
        }
        prev = decoded
        try {
            const next = decodeURIComponent(decoded)
            if (next === decoded) {
                break
            }
            decoded = next
        } catch (e) {
            // UTF-8 として不正なパーセントエスケープ（Shift_JIS でエンコードされた
            // 日本語クエリや末尾の裸の "%" など）は正当な URL にも現れる。
            // ここで失敗扱いにすると master では通っていた URL を一律拒否してしまうため、
            // それまでにデコードできた内容で判定を続行する。
            break
        }
        iterations++
    }
    return { decoded, success: iterations < maxIterations }
}

const HTML_ENTITY_PATTERN =
    /&amp;|&colon;|&tab;|&newline;|&quot;|&lt;|&gt;|&#x([0-9a-f]+);?|&#([0-9]+);?/gi

const HTML_ENTITY_LITERAL_MAP: Record<string, string> = {
    '&amp;': '&',
    '&colon;': ':',
    '&tab;': '\t',
    '&newline;': '\n',
    '&quot;': '"',
    '&lt;': '<',
    '&gt;': '>'
}

function decodeHtmlEntityMatch(
    match: string,
    hex: string | undefined,
    dec: string | undefined
): string {
    if (hex !== undefined) {
        const code = parseInt(hex, 16)
        return Number.isFinite(code) && code <= 0x10ffff
            ? String.fromCodePoint(code)
            : '\uFFFD'
    }
    if (dec !== undefined) {
        const code = parseInt(dec, 10)
        return Number.isFinite(code) && code <= 0x10ffff
            ? String.fromCodePoint(code)
            : '\uFFFD'
    }
    return HTML_ENTITY_LITERAL_MAP[match.toLowerCase()] ?? match
}

/**
 * HTMLエンティティ（&tab; や &#x09; など）をデコードする。
 *
 * 単一パスにすると "java&amp;#115;cript:" のような二重エンコードで
 * "&amp;" → "&" までしか戻らず、危険スキームの検出を取りこぼす。
 * 変化が無くなるまで（上限付きで）繰り返し復元する。
 */
export function decodeHtmlEntities(str: string): string {
    const maxIterations = 5
    let decoded = str
    for (let i = 0; i < maxIterations; i++) {
        const next = decoded.replace(HTML_ENTITY_PATTERN, decodeHtmlEntityMatch)
        if (next === decoded) {
            return decoded
        }
        decoded = next
    }
    return decoded
}

/**
 * クエリパラメータやハッシュに危険なURLスキームが含まれていないかチェックする。
 */
function containsBaseDangerousStartsWith(cleaned: string): boolean {
    return (
        cleaned.startsWith('javascript:') ||
        cleaned.startsWith('vbscript:') ||
        cleaned.startsWith('file:') ||
        cleaned.startsWith('blob:')
    )
}

function containsBaseDangerousSubstring(cleaned: string): boolean {
    return (
        cleaned.includes('javascript:') ||
        cleaned.includes('vbscript:') ||
        cleaned.includes('file:') ||
        cleaned.includes('blob:')
    )
}

function containsDangerousSchemesStartsWithOnly(
    cleaned: string,
    allowDataImage?: boolean
): boolean {
    if (allowDataImage) {
        if (cleaned.startsWith('data:')) {
            return !cleaned.startsWith('data:image/')
        }
        return containsBaseDangerousStartsWith(cleaned)
    }
    return (
        containsBaseDangerousStartsWith(cleaned) || cleaned.startsWith('data:')
    )
}

function containsDangerousSchemesSubstring(
    cleaned: string,
    allowDataImage?: boolean
): boolean {
    if (allowDataImage) {
        if (cleaned.includes('data:')) {
            let idx = cleaned.indexOf('data:')
            while (idx !== -1) {
                if (!cleaned.substring(idx).startsWith('data:image/')) {
                    return true
                }
                idx = cleaned.indexOf('data:', idx + 1)
            }
        }
        return containsBaseDangerousSubstring(cleaned)
    }

    return containsBaseDangerousSubstring(cleaned) || cleaned.includes('data:')
}

/**
 * クエリパラメータやハッシュに危険なURLスキームが含まれていないかチェックする。
 */
export function containsDangerousSchemes(
    value: string,
    options?: { allowDataImage?: boolean; startsWithOnly?: boolean }
): boolean {
    const decodedHtml = decodeHtmlEntities(value)
    const cleaned = decodedHtml
        .toLowerCase()
        .replace(/[\s\x00-\x1F\x7F-\x9F]/g, '')

    if (options?.startsWithOnly) {
        return containsDangerousSchemesStartsWithOnly(
            cleaned,
            options.allowDataImage
        )
    }
    return containsDangerousSchemesSubstring(cleaned, options?.allowDataImage)
}
